import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import AgreementsShell from "@/components/agreements/AgreementsShell";
import DocumentCard from "@/components/agreements/DocumentCard";
import CopyButton from "@/components/agreements/CopyButton";
import { AttorneyBadgeNote } from "@/components/agreements/LegalDisclaimer";
import { BUNDLES, BUNDLE_BY_ID } from "@/data/agreements/bundles";
import { DOC_BY_ID } from "@/data/agreements/documentLibrary";
import { STAGE_BY_ID } from "@/data/agreements/stages";
import { LEVELS } from "@/lib/agreements/recommend";

/**
 * BundleDetail — route: /agreements/bundles/:bundleId
 */

const LEVEL_META = {
  required: {
    label: "Required",
    blurb: "Do not take this step without these.",
    chip: "border-red-200 bg-red-50 text-red-800",
  },
  recommended: {
    label: "Recommended",
    blurb: "Standard for this step; the risk of skipping is real.",
    chip: "border-pink-200 bg-pink-50 text-pink-800",
  },
  situational: {
    label: "Situational",
    blurb: "Only if your specific arrangement calls for it.",
    chip: "border-slate-200 bg-slate-50 text-slate-700",
  },
};

export default function BundleDetail() {
  const { bundleId } = useParams();
  const bundle = BUNDLE_BY_ID[bundleId];

  const grouped = useMemo(() => {
    if (!bundle) return {};
    const out = { required: [], recommended: [], situational: [] };
    for (const entry of bundle.docs || []) {
      const doc = DOC_BY_ID[entry.id];
      if (!doc) continue;
      const level = out[entry.level] ? entry.level : "situational";
      out[level].push({ doc, level });
    }
    return out;
  }, [bundle]);

  const resolved = useMemo(
    () => LEVELS.reduce((n, level) => n + (grouped[level]?.length || 0), 0),
    [grouped],
  );

  const bundleText = useMemo(() => (bundle ? buildBundleText(bundle, grouped) : ""), [bundle, grouped]);

  /* ── Unknown bundle: show every bundle rather than an error ─────────────── */
  if (!bundle) {
    return (
      <AgreementsShell
        crumbs={[{ label: "Bundles", to: "/agreements/bundles" }, { label: "Not found" }]}
        title="That bundle does not exist"
        subtitle="The link may be out of date. Here is every bundle we have."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          {BUNDLES.map((b) => (
            <Link key={b.id} to={`/agreements/bundles/${b.id}`} className="rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <Card className="h-full transition-transform hover:-translate-y-0.5">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h2 className="font-semibold leading-tight">{b.title}</h2>
                    {b.attorneyLed && (
                      <Badge className="shrink-0 border-pink-300 bg-pink-100 font-normal text-pink-900 hover:bg-pink-100">
                        Attorney-led
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{b.blurb}</p>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium">
                    {(b.docs || []).filter((d) => DOC_BY_ID[d.id]).length} documents <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </AgreementsShell>
    );
  }

  const stage = STAGE_BY_ID[bundle.stage];

  return (
    <AgreementsShell
      wide
      crumbs={[{ label: "Bundles", to: "/agreements/bundles" }, { label: bundle.title }]}
      title={bundle.title}
      subtitle={bundle.blurb}
      actions={
        <>
          <CopyButton text={bundleText} label="Copy list" variant="outline" />
          <Button asChild>
            <Link to="/agreements/intake">Build my own plan</Link>
          </Button>
        </>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {stage && <Badge variant="outline" className="font-normal">{stage.name}</Badge>}
        <Badge variant="outline" className="font-normal">
          {resolved} document{resolved === 1 ? "" : "s"}
        </Badge>
        {LEVELS.map((level) =>
          (grouped[level]?.length || 0) > 0 ? (
            <span key={level} className={cn("rounded-full border px-2.5 py-0.5 text-xs font-medium", LEVEL_META[level].chip)}>
              {grouped[level].length} {LEVEL_META[level].label.toLowerCase()}
            </span>
          ) : null,
        )}
      </div>

      {/* Why this bundle */}
      {bundle.whyThisBundle && (
        <section className="mb-5 rounded-xl border border-indigo-200 bg-indigo-50 p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
            <Lightbulb className="h-4 w-4 text-indigo-600" /> Why this bundle
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-indigo-950">{bundle.whyThisBundle}</p>
        </section>
      )}

      {bundle.attorneyLed && (
        <AttorneyBadgeNote
          level="strongly-recommended"
          note="This bundle exists so you understand what you are signing and arrive at your attorney prepared — not so you can paper it yourself."
          className="mb-5"
        />
      )}

      {resolved === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-sm text-muted-foreground">
            None of this bundle's documents resolve in the library right now.{" "}
            <Link className="underline" to="/agreements/library">
              Browse the library
            </Link>{" "}
            instead.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {LEVELS.map((level) => {
            const list = grouped[level] || [];
            if (list.length === 0) return null;
            const meta = LEVEL_META[level];
            return (
              <section key={level}>
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <h2 className="text-base font-semibold">{meta.label}</h2>
                  <span className={cn("rounded-full border px-2 py-0.5 text-xs font-medium", meta.chip)}>{list.length}</span>
                </div>
                <p className="mb-3 text-xs text-muted-foreground">{meta.blurb}</p>
                <div className="grid gap-3 md:grid-cols-2">
                  {list.map(({ doc }) => (
                    <DocumentCard key={doc.id} doc={doc} level={level} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <div className="mt-8">
        <AttorneyBadgeNote
          level="yes"
          note="A bundle is a starting list, not a substitute for reading each entry. Which of these you actually need depends on the deal in front of you."
        />
      </div>
    </AgreementsShell>
  );
}

function buildBundleText(bundle, grouped) {
  const lines = [];
  lines.push(bundle.title.toUpperCase());
  lines.push(bundle.blurb || "");
  if (bundle.attorneyLed) lines.push("ATTORNEY-LED BUNDLE");
  lines.push("");
  if (bundle.whyThisBundle) {
    lines.push("Why this bundle:");
    lines.push(bundle.whyThisBundle);
    lines.push("");
  }
  lines.push(
    "Educational and informational only. Not legal advice. Have important agreements reviewed by a qualified attorney before signing.",
  );
  lines.push("");
  for (const level of LEVELS) {
    const list = grouped[level] || [];
    if (list.length === 0) continue;
    lines.push("─".repeat(70));
    lines.push(`${LEVEL_META[level].label.toUpperCase()} (${list.length})`);
    lines.push("");
    for (const { doc } of list) {
      lines.push(`• ${doc.name}`);
      if (doc.whenYouNeedIt) lines.push(`    when: ${doc.whenYouNeedIt}`);
      if (doc.attorneyReview === "strongly-recommended") lines.push("    attorney involvement strongly recommended");
      if (doc.templateId) lines.push("    template available");
      lines.push("");
    }
  }
  return lines.join("\n");
}
